import { Checkbox } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import {
  AI_QUOTA_PAYWALL_PRICE_PER_SEAT_ANNUAL,
  AI_QUOTA_PAYWALL_PRICE_PER_SEAT_MONTHLY,
  RoleTypeEnum,
} from "@superblocksteam/shared";
import type { Billing, OrganizationUserDto } from "@superblocksteam/shared";

import { MANAGE_USERS } from "constants/rbac";
import { Modal } from "core/components/Modal";
import { useAuthorizationCheck } from "hooks/ui/rbac/useAuthorizationCheck";
import { sendOrgInvitation } from "pages/components/invitation/utils";
import { selectOnlyOrganization } from "store/slices/organizations";
import {
  useGetPlanSummaryQuery,
  useUpdateSubscriptionMutation,
} from "store/slices/reduxApi/billing";
import {
  useAssignBuilderSeatMutation,
  useListBuilderSeatsQuery,
} from "store/slices/reduxApi/builderSeats";
import { useListRolesQuery } from "store/slices/reduxApi/rbac";
import { useListUsersQuery } from "store/slices/reduxApi/users";
import type { HttpError } from "store/utils/types";

import {
  INVITE_AND_ASSIGN_DEFAULT_ORG_ROLE_NAME,
  isValidInviteEmail,
  normalizeInviteEmail,
} from "./assignBuilderSeatsInvite";

type AssignBuilderSeatsModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** When true, show banner indicating current user does not have an AI Builder seat (when opened from paywall flow) */
  showNoSeatBanner?: boolean;
};

function getAssignableUsers(
  orgUsers: OrganizationUserDto[] | undefined,
  builderUserIds: Set<string>,
  builderEmails: Set<string>,
): OrganizationUserDto[] {
  if (!orgUsers) return [];
  return orgUsers.filter((u) => {
    const hasSeat =
      builderUserIds.has(u.id) ||
      builderEmails.has(u.email.trim().toLowerCase());
    return !hasSeat;
  });
}

type ConfirmSnapshot = {
  selectedCount: number;
  additionalSeatsToPurchase: number;
  totalAddedPrice: number;
  priceSuffix: string;
  selectedUserNames: string[];
};

export function AssignBuilderSeatsModal({
  open,
  onClose,
  onSuccess,
  showNoSeatBanner = false,
}: AssignBuilderSeatsModalProps) {
  const org = useSelector(selectOnlyOrganization);
  const { data: planSummary } = useGetPlanSummaryQuery(undefined, {
    skip: !open,
  });
  const cancelAt = org?.billing?.cancelAt
    ? new Date(org.billing.cancelAt as unknown as string | number)
    : null;
  const isPendingCancellation =
    cancelAt != null && cancelAt.getTime() > Date.now();
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [assignErrors, setAssignErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | undefined>(undefined);
  /**
   * IDs of users invited on-the-fly that have not yet appeared in
   * `assignableUsers`. We use a Set (not a single id) because the admin can
   * fire off multiple invites back-to-back faster than `refetchOrgUsers`
   * resolves; a single ref would be overwritten by the second invite and the
   * pruning effect below would then silently deselect the first invitee. Each
   * id is removed from this set once the user shows up in `assignableUsers`
   * (see the scroll-into-view effect).
   */
  const newUserIdsToFocus = useRef<Set<string>>(new Set<string>());
  const [confirmView, setConfirmView] = useState(false);
  const [confirmSnapshot, setConfirmSnapshot] =
    useState<ConfirmSnapshot | null>(null);

  const { data: builderSeatsData, refetch: refetchBuilderSeats } =
    useListBuilderSeatsQuery(undefined, {
      skip: !open,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });
  const { data: orgUsers, refetch: refetchOrgUsers } = useListUsersQuery(
    { organizationId: org?.id ?? "" },
    { skip: !open || !org?.id },
  );

  const [canManageUsers] = useAuthorizationCheck([MANAGE_USERS]);

  const { data: orgRoles } = useListRolesQuery(
    {
      organizationId: org?.id ?? "",
      type: RoleTypeEnum.ORGANIZATION,
    },
    { skip: !open || !org?.id },
  );

  const developerRoleId = useMemo(
    () =>
      orgRoles?.find((r) => r.name === INVITE_AND_ASSIGN_DEFAULT_ORG_ROLE_NAME)
        ?.id,
    [orgRoles],
  );

  const builderUserIds = useMemo(
    () => new Set(builderSeatsData?.builders?.map((b) => b.userId) ?? []),
    [builderSeatsData],
  );
  const builderEmails = useMemo(
    () =>
      new Set(
        builderSeatsData?.builders?.map((b) => b.email.trim().toLowerCase()) ??
          [],
      ),
    [builderSeatsData],
  );
  const assignableUsers = useMemo(
    () => getAssignableUsers(orgUsers, builderUserIds, builderEmails),
    [orgUsers, builderUserIds, builderEmails],
  );
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return assignableUsers;
    const q = search.trim().toLowerCase();
    return assignableUsers.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q),
    );
  }, [assignableUsers, search]);

  const searchNormalized = useMemo(
    () => normalizeInviteEmail(search),
    [search],
  );

  const orgUserMatchingSearch = useMemo(() => {
    if (!searchNormalized || !orgUsers) return undefined;
    return orgUsers.find(
      (u) => normalizeInviteEmail(u.email) === searchNormalized,
    );
  }, [orgUsers, searchNormalized]);

  const searchMatchesUserWhoAlreadyHasSeat = useMemo(() => {
    if (!orgUserMatchingSearch) return false;
    return (
      builderUserIds.has(orgUserMatchingSearch.id) ||
      builderEmails.has(orgUserMatchingSearch.email.trim().toLowerCase())
    );
  }, [builderEmails, builderUserIds, orgUserMatchingSearch]);

  const showInviteAffordance = useMemo(() => {
    if (!canManageUsers || !developerRoleId) return false;
    if (!isValidInviteEmail(search)) return false;
    if (filteredUsers.length > 0) return false;
    if (searchMatchesUserWhoAlreadyHasSeat) return false;
    return true;
  }, [
    canManageUsers,
    developerRoleId,
    filteredUsers.length,
    search,
    searchMatchesUserWhoAlreadyHasSeat,
  ]);

  const [assign, { isLoading: isAssigning }] = useAssignBuilderSeatMutation();
  const [updateSubscription, { isLoading: isUpdatingSubscription }] =
    useUpdateSubscriptionMutation();

  useEffect(() => {
    setSelectedIds((prev) => {
      // Spare every user we just invited on-the-fly: `refetchOrgUsers` is
      // fire-and-forget, so `assignableUsers` may update (e.g. from
      // `builderSeatsData` refetching) before `orgUsers` includes the new
      // invitees. Without this guard, the pruning effect silently deselects
      // them and the admin ends up submitting without the users they just
      // invited. Each id is removed from the set by the scroll-into-view
      // effect below once that user actually appears in `assignableUsers`.
      const pendingInvitees = newUserIdsToFocus.current;
      const next = new Set(
        Array.from(prev).filter(
          (userId) =>
            pendingInvitees.has(userId) ||
            assignableUsers.some((user) => user.id === userId),
        ),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [assignableUsers]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      void refetchBuilderSeats();
    }, 1000);
    return () => clearTimeout(timer);
  }, [open, refetchBuilderSeats]);

  const handleToggle = useCallback((userId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(userId);
      else next.delete(userId);
      return next;
    });
    setAssignErrors([]);
  }, []);

  const handleInviteAndAssign = useCallback(async () => {
    if (!org?.id || !developerRoleId) return;
    const email = normalizeInviteEmail(search);
    if (!email || !isValidInviteEmail(email)) return;
    setIsInviting(true);
    setInviteError(undefined);
    setAssignErrors([]);
    try {
      let httpError: HttpError | undefined;
      // `sendOrgInvitation` can resolve with `undefined` if the underlying
      // HTTP helper catches the error and the `.catch` handler returns void.
      // Guard against that before reading fields off the response.
      const res = await sendOrgInvitation({
        userEmails: [email],
        organizationId: org.id,
        roleId: developerRoleId,
        onError: (e) => {
          httpError = e;
        },
      });
      if (httpError || !res || res.error) {
        setInviteError(
          httpError?.message ?? res?.error ?? "Failed to send invite.",
        );
        return;
      }
      const newUser = res.users?.[0];
      if (!newUser) {
        setInviteError("Invite succeeded but no user was returned.");
        return;
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.add(newUser.id);
        return next;
      });
      newUserIdsToFocus.current.add(newUser.id);
      setSearch("");
      void refetchOrgUsers();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to send invite.";
      setInviteError(message);
    } finally {
      setIsInviting(false);
    }
  }, [developerRoleId, org?.id, refetchOrgUsers, search]);

  // Clear the stale invite error as soon as the admin edits the search.
  // The `if (inviteError)` guard keeps this a no-op when there is nothing
  // to clear. `inviteError` is intentionally NOT in the dep array: if it
  // were, setting an error would immediately trip this effect and wipe the
  // message before the user could read it.
  useEffect(() => {
    if (inviteError) setInviteError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // After successful invites, once each new user appears in the list, scroll
  // their row into view and remove the id from the pending-focus set. We
  // iterate in insertion order so when several invitees land in the same
  // refetch, the most recent one wins the final scroll position.
  useEffect(() => {
    const pending = newUserIdsToFocus.current;
    if (pending.size === 0) return;
    for (const id of Array.from(pending)) {
      if (!assignableUsers.some((u) => u.id === id)) continue;
      const el =
        typeof document !== "undefined"
          ? document.querySelector<HTMLElement>(`[data-user-row-id="${id}"]`)
          : null;
      el?.scrollIntoView({ block: "nearest" });
      pending.delete(id);
    }
  }, [assignableUsers]);

  const handleClose = useCallback(() => {
    setSearch("");
    setSelectedIds(new Set());
    setAssignErrors([]);
    setSubmitting(false);
    setIsInviting(false);
    setInviteError(undefined);
    newUserIdsToFocus.current.clear();
    setConfirmView(false);
    setConfirmSnapshot(null);
    onClose();
  }, [onClose]);

  const selectedCount = selectedIds.size;
  const assignedSeatCount = builderSeatsData?.builders.length ?? 0;
  const currentTotalSeats =
    builderSeatsData?.totalPurchasedSeats ?? assignedSeatCount;
  const availableSeatsLeft = Math.max(currentTotalSeats - assignedSeatCount, 0);
  const additionalSeatsToPurchase = Math.max(
    selectedCount - availableSeatsLeft,
    0,
  );
  const newTotalSeats = currentTotalSeats + additionalSeatsToPurchase;
  const billing = org?.billing as Billing | undefined;
  const billingInterval =
    (billing as Billing & { billingInterval?: string; interval?: string })
      ?.billingInterval === "annual" ||
    (billing as Billing & { billingInterval?: string; interval?: string })
      ?.interval === "annual"
      ? "annual"
      : "monthly";
  const perSeatPrice =
    billingInterval === "annual"
      ? planSummary?.pricePerSeatAnnual != null
        ? planSummary.pricePerSeatAnnual / 12
        : AI_QUOTA_PAYWALL_PRICE_PER_SEAT_ANNUAL
      : (planSummary?.pricePerSeatMonthly ??
        AI_QUOTA_PAYWALL_PRICE_PER_SEAT_MONTHLY);
  const totalAddedPrice = additionalSeatsToPurchase * perSeatPrice;
  const priceSuffix = "/month";

  // Enterprise contracts have no Stripe subscription. Instead of "Purchase &
  // Assign" through Stripe, we allow overflow assignments when the contract
  // permits it; the server logs a `seat_overage` entry on contract_change_log
  // for manual invoicing.
  const isEnterprise = planSummary?.contractType != null;
  const seatsUnlimited = planSummary?.seatsUnlimited === true;
  const overageSeatsEnabled = planSummary?.overageSeatsEnabled === true;
  const needsStripePurchase = additionalSeatsToPurchase > 0 && !isEnterprise;
  const willIncurOverage =
    additionalSeatsToPurchase > 0 &&
    isEnterprise &&
    overageSeatsEnabled &&
    !seatsUnlimited;
  const enterpriseSeatsBlocked =
    additionalSeatsToPurchase > 0 &&
    isEnterprise &&
    !overageSeatsEnabled &&
    !seatsUnlimited;

  // Reset submit-related UI state and surface an error banner. Used both from
  // known failure branches inside `handleSubmit` and from the top-level catch
  // that defends against unexpected rejections (e.g. `refetchBuilderSeats`).
  const abortSubmit = useCallback((errors: string[]) => {
    setAssignErrors(errors);
    setSubmitting(false);
    setConfirmView(false);
    setConfirmSnapshot(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setSubmitting(true);
    setAssignErrors([]);

    try {
      if (needsStripePurchase) {
        try {
          await updateSubscription({
            unitType: "builder_seat",
            quantity: newTotalSeats,
            billingInterval,
          }).unwrap();

          for (let attempt = 0; attempt < 8; attempt += 1) {
            const refreshed = await refetchBuilderSeats();
            const refreshedTotalSeats =
              refreshed.data?.totalPurchasedSeats ?? 0;
            if (refreshedTotalSeats >= newTotalSeats) {
              break;
            }
            await new Promise((resolve) => {
              window.setTimeout(resolve, 750);
            });
          }
        } catch (err: unknown) {
          const data = (err as { data?: { message?: string } })?.data;
          abortSubmit([
            data?.message ?? "Failed to update license subscription",
          ]);
          return;
        }
      }

      const errors: string[] = [];
      for (const userId of selectedIds) {
        try {
          await assign({ userId }).unwrap();
        } catch (err: unknown) {
          const data = (err as { data?: { code?: string; message?: string } })
            ?.data;
          const code = data?.code;
          const msg =
            code === "NO_AVAILABLE_SEATS"
              ? "No available licenses"
              : code === "ALREADY_HAS_SEAT"
                ? "User already has a license"
                : code === "NOT_A_DEVELOPER"
                  ? "User is not a Developer"
                  : (data?.message ?? "Failed to assign license");
          errors.push(msg);
        }
      }
      if (errors.length > 0) {
        abortSubmit(errors);
        return;
      }

      setSearch("");
      setSelectedIds(new Set());
      setAssignErrors([]);
      setSubmitting(false);
      setConfirmView(false);
      setConfirmSnapshot(null);
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (err: unknown) {
      // Top-level safety net: any unexpected rejection (e.g.
      // `refetchBuilderSeats` during polling, a non-HTTP throw) must still
      // leave the modal in a usable state rather than frozen with
      // `submitting === true`.
      const message =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      abortSubmit([message]);
    }
  }, [
    abortSubmit,
    needsStripePurchase,
    assign,
    billingInterval,
    newTotalSeats,
    onClose,
    onSuccess,
    refetchBuilderSeats,
    selectedIds,
    updateSubscription,
  ]);

  const isSubmitting = submitting || isAssigning || isUpdatingSubscription;
  const submitLabel =
    selectedCount === 0
      ? "Assign"
      : willIncurOverage
        ? `Assign (${selectedCount}) & Incur Overage`
        : needsStripePurchase
          ? `Purchase (${additionalSeatsToPurchase}) & Assign (${selectedCount})`
          : `Assign (${selectedCount})`;

  const openConfirm = useCallback(() => {
    const selectedUserNames = assignableUsers
      .filter((u) => selectedIds.has(u.id))
      .map((u) => u.name || u.email);
    setConfirmSnapshot({
      selectedCount,
      additionalSeatsToPurchase,
      totalAddedPrice,
      priceSuffix,
      selectedUserNames,
    });
    setConfirmView(true);
  }, [
    selectedCount,
    additionalSeatsToPurchase,
    totalAddedPrice,
    priceSuffix,
    assignableUsers,
    selectedIds,
  ]);

  return (
    <Modal
      open={open}
      onClose={() => {
        if (isSubmitting) return;
        if (confirmView) {
          setConfirmView(false);
          setConfirmSnapshot(null);
          return;
        }
        handleClose();
      }}
      aria-label="Assign AI Builder licenses"
      closable={false}
      className="w-120 gap-0 rounded-2xl"
      bodyClassName="overflow-hidden rounded-2xl p-0"
    >
      {confirmView && confirmSnapshot ? (
        <div className="flex flex-col bg-white">
          <div className="flex items-center gap-1.5 border-b border-[#e8eaed] px-6 py-4">
            <h3 className="!m-0 flex-1 text-[15px] leading-5 font-semibold tracking-[-0.3px] text-[#1f2633]">
              Confirm License assignment
            </h3>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                setConfirmView(false);
                setConfirmSnapshot(null);
              }}
              className="flex cursor-pointer items-center justify-center rounded border-0 bg-transparent p-1 text-[#6c7689] transition hover:bg-[#f3f4f6] disabled:opacity-50"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 4 4 12M4 4l8 8" />
              </svg>
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4 pb-4">
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              {confirmSnapshot.additionalSeatsToPurchase > 0 && (
                <div className="flex items-center justify-between gap-4">
                  <span>Purchase additional licenses</span>
                  <span>
                    {confirmSnapshot.additionalSeatsToPurchase} (
                    {`$${confirmSnapshot.totalAddedPrice}${confirmSnapshot.priceSuffix}`}
                    )
                  </span>
                </div>
              )}
              <div className="flex items-start justify-between gap-4">
                <span className="shrink-0 pt-0.5">
                  New AI Builder licenses (
                  {confirmSnapshot.selectedUserNames.length})
                </span>
                <div className="flex min-w-0 flex-1 flex-col items-end gap-1">
                  {confirmSnapshot.selectedUserNames.map((name) => (
                    <span
                      key={name}
                      className="max-w-full truncate text-sm font-medium text-gray-900"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            {assignErrors.length > 0 && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] leading-snug text-red-700">
                {assignErrors.slice(0, 3).join("; ")}
                {assignErrors.length > 3 &&
                  ` (+${assignErrors.length - 3} more)`}
              </div>
            )}
          </div>
          <div className="shrink-0 border-t border-gray-200 px-6 py-4">
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  setConfirmView(false);
                  setConfirmSnapshot(null);
                }}
                className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  void handleSubmit();
                }}
                className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting && (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                )}
                {isSubmitting ? "Confirming..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col bg-white">
          <div className="flex items-center gap-1.5 border-b border-[#e8eaed] px-6 py-4">
            <h2 className="!m-0 flex-1 text-[15px] leading-5 font-semibold tracking-[-0.3px] text-[#1f2633]">
              Assign AI Builder licenses
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="flex cursor-pointer items-center justify-center rounded border-0 bg-transparent p-1 text-[#6c7689] transition hover:bg-[#f3f4f6]"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 4 4 12M4 4l8 8" />
              </svg>
            </button>
          </div>
          <div className="px-6 pt-2 pb-1">
            <div className="text-sm text-gray-500">
              {availableSeatsLeft === 0 ? (
                <>
                  <span className="font-medium text-amber-600">
                    License limit reached.
                  </span>{" "}
                  {assignedSeatCount}/{currentTotalSeats} assigned
                </>
              ) : (
                <>
                  {availableSeatsLeft}{" "}
                  {availableSeatsLeft === 1 ? "license" : "licenses"} available
                </>
              )}
            </div>
          </div>

          {showNoSeatBanner ? (
            <div className="mx-5 mb-3 rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
              <p className="m-0 font-medium">
                You don&apos;t have an AI Builder license
              </p>
              <p className="m-0 mt-0.5">
                Assign a license to yourself below to start building with Clark
                AI.
              </p>
            </div>
          ) : null}

          <div className="px-5 pb-3">
            <div className="relative">
              <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-gray-400">
                <svg
                  aria-hidden="true"
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-full rounded-md border border-gray-200 bg-white pr-9 pl-9 text-sm text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition outline-none focus:border-gray-300 focus:ring-[3px] focus:ring-gray-200/70"
              />
              {search && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  className="absolute top-1/2 right-2 flex -translate-y-1/2 cursor-pointer items-center justify-center rounded border-0 bg-transparent p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 4 4 12M4 4l8 8" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {assignErrors.length > 0 && (
            <div className="mx-5 mb-3 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] leading-snug text-red-700">
              {assignErrors.slice(0, 3).join("; ")}
              {assignErrors.length > 3 && ` (+${assignErrors.length - 3} more)`}
            </div>
          )}

          <div className="max-h-[340px] min-h-0 flex-1 overflow-y-auto px-5 pb-3">
            {assignableUsers.length === 0 && !showInviteAffordance ? (
              <p className="py-6 text-center text-sm text-gray-500">
                All users have already been assigned licenses.
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                {filteredUsers.map((u) => (
                  <label
                    key={u.id}
                    data-user-row-id={u.id}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-1 py-2.5 transition hover:bg-gray-50"
                  >
                    <Checkbox
                      checked={selectedIds.has(u.id)}
                      onChange={(e) => handleToggle(u.id, e.target.checked)}
                    />
                    <div className="flex min-w-0 flex-1 items-center">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-950">
                          {u.name ?? u.email}
                        </div>
                        {u.email && u.name && (
                          <div className="text-xs text-gray-500">{u.email}</div>
                        )}
                      </div>
                      {u.assignments?.[0]?.role?.name ? (
                        <span className="ml-2 inline-flex shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                          {u.assignments[0].role.name}
                        </span>
                      ) : null}
                    </div>
                  </label>
                ))}
                {filteredUsers.length === 0 &&
                  searchMatchesUserWhoAlreadyHasSeat && (
                    <p className="py-6 text-center text-sm text-gray-500">
                      This user already has an AI Builder license.
                    </p>
                  )}
                {filteredUsers.length === 0 &&
                  !searchMatchesUserWhoAlreadyHasSeat &&
                  showInviteAffordance && (
                    <div className="flex flex-col gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <span className="min-w-0 truncate text-sm text-gray-900">
                          {search.trim()}
                        </span>
                        <button
                          type="button"
                          data-test="assign-builder-invite-affordance"
                          disabled={isInviting}
                          className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-900 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => {
                            void handleInviteAndAssign();
                          }}
                        >
                          {isInviting && (
                            <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                          )}
                          {isInviting ? "Inviting..." : "Invite and assign"}
                        </button>
                      </div>
                      {inviteError && (
                        <p className="m-0 text-[12px] leading-snug text-red-600">
                          {inviteError}
                        </p>
                      )}
                    </div>
                  )}
                {filteredUsers.length === 0 &&
                  !searchMatchesUserWhoAlreadyHasSeat &&
                  !showInviteAffordance &&
                  assignableUsers.length > 0 && (
                    <p className="py-6 text-center text-sm text-gray-500">
                      No users found.
                    </p>
                  )}
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 px-5 py-4">
            {needsStripePurchase && isPendingCancellation && (
              <div className="mb-4 rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                Your plan is pending cancellation. Purchasing additional
                licenses is disabled until the cancellation is reversed.
              </div>
            )}
            {needsStripePurchase && !isPendingCancellation && (
              <div className="mb-4 rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                {additionalSeatsToPurchase}{" "}
                {additionalSeatsToPurchase === 1 ? "license" : "licenses"} x $
                {perSeatPrice}
                {priceSuffix} = ${totalAddedPrice}
                {priceSuffix} added to your plan.
              </div>
            )}
            {willIncurOverage && (
              <div className="mb-4 rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                Assigning {additionalSeatsToPurchase} additional{" "}
                {additionalSeatsToPurchase === 1 ? "license" : "licenses"} will
                exceed your contracted seats and will be invoiced as overage per
                your contract terms.
              </div>
            )}
            {enterpriseSeatsBlocked && (
              <div className="mb-4 rounded border border-red-200 bg-red-50 p-2 text-xs leading-4 text-red-700">
                Assigning these licenses would exceed your contracted seats.
                Contact your account manager to add more seats.
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  selectedCount === 0 ||
                  enterpriseSeatsBlocked ||
                  isSubmitting ||
                  (needsStripePurchase && isPendingCancellation)
                }
                onClick={
                  needsStripePurchase
                    ? openConfirm
                    : () => {
                        void handleSubmit();
                      }
                }
                className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting && !needsStripePurchase && (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                )}
                {isSubmitting && !needsStripePurchase
                  ? "Assigning..."
                  : submitLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
